import { useState } from "react";
import { ShieldCheck, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useApp } from "../lib/AppContext";

/**
 * One-time GDPR privacy notice popup.
 * Shown to any user whose `gdpr_notice_seen_at` is NULL on /auth/me.
 * Dismissed permanently by clicking "Ho capito" (POST /api/gdpr/notice-seen).
 *
 * Placement: rendered globally by Shell after auth resolves.
 */
export default function GdprNoticeModal() {
  const { user, refresh } = useApp();
  const [dismissing, setDismissing] = useState(false);
  const [visible, setVisible] = useState(true);

  if (!user) return null;
  if (user.gdpr_notice_seen_at) return null;
  if (!visible) return null;

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.gdprMarkNoticeSeen();
      setVisible(false);
      await refresh();
    } catch (e) {
      toast.error("Errore, riprova");
    } finally {
      setDismissing(false);
    }
  };

  return (
    <div
      role="dialog" aria-modal="true"
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/40 backdrop-blur-sm"
      data-testid="gdpr-notice-modal"
    >
      <div className="pxos-card max-w-lg w-full p-6 sm:p-7 space-y-4 relative">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-emerald-100 p-2">
            <ShieldCheck size={22} className="text-emerald-700" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-xl font-semibold" data-testid="gdpr-notice-title">
              Come trattiamo i tuoi dati
            </h2>
            <p className="text-xs opacity-60 mt-0.5">Informativa GDPR · una tantum</p>
          </div>
        </div>

        <div className="text-sm space-y-3 opacity-90 leading-relaxed">
          <p>
            Questa applicazione conserva i tuoi dati (agenda, finanze, documenti,
            CRM, memorie della Segretaria IA) su un database dedicato e cifrato.
            L'accesso è isolato per singolo utente tramite <strong>Row-Level Security</strong> Postgres.
          </p>
          <p>
            In ogni momento puoi:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-sm">
            <li><strong>Esportare i tuoi dati</strong> in formato ZIP (GDPR art. 20).</li>
            <li><strong>Eliminare l'account</strong> con periodo di grazia di 7-30 giorni (art. 17).</li>
            <li>Consultare l'<strong>audit log</strong> delle attività recenti.</li>
          </ul>
          <p>
            Trovi tutto in <strong>Impostazioni → Privacy e dati</strong>.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t"
             style={{ borderColor: "var(--pxos-border)" }}>
          <a
            href="/privacy" target="_blank" rel="noreferrer"
            className="text-xs underline opacity-75 hover:opacity-100 inline-flex items-center gap-1"
            data-testid="gdpr-notice-privacy-link"
          >
            Informativa completa <ExternalLink size={11} />
          </a>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={dismiss} disabled={dismissing}
              data-testid="gdpr-notice-dismiss-btn"
              className="pxos-btn-primary text-sm px-5 py-2 disabled:opacity-50"
            >
              {dismissing ? "…" : "Ho capito"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
